import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { Dot } from './icons'

/**
 * How often an open portal records that its person is still there (users/{uid}.lastSeenAt), and how recent that
 * must be to count as "active now". A little over two beats, so one missed write doesn't flicker someone offline.
 */
export const HEARTBEAT_MS = 60_000
export const ACTIVE_NOW_MS = 150_000

export type ActivityTimes = { lastSeenAt?: number | null; lastSignInAt?: number | null }

/** The current time, refreshed on an interval so "active now" and "5 min ago" age without a reload. */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), everyMs)
    return () => window.clearInterval(t)
  }, [everyMs])
  return now
}

function timeAgo(ms: number, now: number): string {
  const min = Math.floor((now - ms) / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hr ago`
  const days = Math.floor(hr / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear()
  return `on ${new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' })}`
}

/** The exact moment: "4:15 PM" today, "Oct 2, 4:15 PM" this year, with the year added for older dates. */
function exactTime(ms: number, now: number): string {
  const d = new Date(ms)
  const n = new Date(now)
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (d.toDateString() === n.toDateString()) return time
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === n.getFullYear() ? undefined : 'numeric' })
  return `${date}, ${time}`
}

/** "Active now", or "Last active 3 hr ago (Oct 2, 4:15 PM)", followed by when they last signed in. For staff views of a person. */
export function ActivityLine({ times, className }: { times: ActivityTimes | null | undefined; className?: string }) {
  const now = useNow(30_000)
  const seen = times?.lastSeenAt ?? null
  const signedIn = times?.lastSignInAt ?? null
  if (!seen && !signedIn) return <span className={clsx('text-small text-dim', className)}>No activity yet</span>
  const activeNow = seen !== null && now - seen < ACTIVE_NOW_MS
  return (
    <span className={clsx('text-small text-dim', className)}>
      {activeNow ? (
        <span className="inline-flex items-center gap-1.5 font-medium text-ok">
          <Dot size={6} />
          Active now
        </span>
      ) : (
        // Each time stays on one line; on a phone the line wraps between the parts instead.
        <>
          Last active {timeAgo((seen ?? signedIn)!, now)} <span className="whitespace-nowrap">({exactTime((seen ?? signedIn)!, now)})</span>
        </>
      )}
      {signedIn !== null && <> · <span className="whitespace-nowrap">signed in {exactTime(signedIn, now)}</span></>}
    </span>
  )
}

/**
 * A "Last active" table cell: "Active now" or "3 hr ago" with the exact time, then when they last signed in.
 * `invited` is someone with access who has never signed in.
 */
export function LastActiveCell({ times, invited }: { times: ActivityTimes | null | undefined; invited?: boolean }) {
  const now = useNow(30_000)
  const seen = times?.lastSeenAt ?? times?.lastSignInAt ?? null
  const signedIn = times?.lastSignInAt ?? null
  if (seen === null) return <span className="text-small text-dim">{invited ? 'Hasn’t signed in' : 'No activity yet'}</span>
  const ago = timeAgo(seen, now).replace(/^on /, '')
  return (
    <span className="grid gap-0.5">
      {now - seen < ACTIVE_NOW_MS ? (
        <span className="inline-flex items-center gap-1.5 text-small font-medium text-ok">
          <Dot size={6} />
          Active now
        </span>
      ) : (
        <span className="text-small text-ink">
          {ago.charAt(0).toUpperCase() + ago.slice(1)}
          <span className="block text-[0.75rem] text-dim">{exactTime(seen, now)}</span>
        </span>
      )}
      {signedIn !== null && <span className="text-[0.75rem] text-dim">Signed in {exactTime(signedIn, now)}</span>}
    </span>
  )
}
